import { LoadingButton, Text } from "@clipboard-health/ui-react";
import { isDefined } from "@clipboard-health/util-ts";
import { Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { SUPPORT_WEBFORM_URL } from "@src/appV2/ContactCBHInfo/constants";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { ExternalLink } from "@src/appV2/lib/Links/ExternalLink";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { useCreateWorkplaceAgreement } from "@src/appV2/WorkplaceAgreement/api/useCreateWorkplaceAgreement";
import { useGetWorkplaceAgreements } from "@src/appV2/WorkplaceAgreement/api/useGetWorkplaceAgreements";
import { isEmpty } from "lodash";
import { useState } from "react";

export function WorkplaceAgreementDialog() {
  const session = useSession();
  const { admin: isAdmin, profile, user } = session ?? { profile: undefined, user: undefined };
  const workplaceId = profile?.userId;
  const userId = user?._id ?? "";

  const { variation, flexAgreementUrl, hiringAgreementUrl } = useCbhFlag(
    CbhFeatureFlag.REQUIRE_WORKPLACE_AGREEMENT,
    {
      defaultValue: {
        variation: "off",
        flexAgreementUrl: "",
        hiringAgreementUrl: "",
      },
    }
  );

  const requireWorkplaceAgreement =
    isDefined(workplaceId) && !isAdmin && !isEmpty(userId) && variation === "on";
  const {
    data: workplaceAgreements,
    isLoading: isLoadingWorkplaceAgreements,
    isError: isErrorGettingWorkplaceAgreements,
  } = useGetWorkplaceAgreements(workplaceId, {
    enabled: requireWorkplaceAgreement,
  });
  const { mutateAsync: createWorkplaceAgreement, isLoading: isLoadingCreateWorkplaceAgreement } =
    useCreateWorkplaceAgreement(workplaceId);

  const [visible, setVisible] = useState<boolean>(true);
  const onAgree = async () => {
    try {
      await createWorkplaceAgreement({
        workplaceId,
        acceptedByUserId: userId,
        acceptedAt: new Date(),
        arbitrationAgreementVersionNumber: 1,
      });
      setVisible(false);
    } catch {
      showErrorToast("Failed to accept agreement. Please try again or contact support.");
    }
  };

  if (!requireWorkplaceAgreement) {
    return null;
  }

  if (
    isErrorGettingWorkplaceAgreements ||
    isLoadingWorkplaceAgreements ||
    !isEmpty(workplaceAgreements?.data)
  ) {
    return null;
  }

  return (
    <Dialog disableEscapeKeyDown fullWidth open={visible} maxWidth="md">
      <DialogTitle sx={{ textAlign: "center" }}>Marketplace Access Agreements</DialogTitle>
      <DialogContent>
        <Stack spacing={2} alignItems="center" textAlign="center">
          <Text variant="body1">
            Please carefully read our{" "}
            <ExternalLink
              to="https://www.clipboardhealth.com/terms-of-service?menu=none"
              underline="hover"
            >
              Terms of Service
            </ExternalLink>{" "}
            and Marketplace Access Agreements. By clicking &quot;Agree and Accept,&quot; I agree to
            Clipboard&apos;s Terms of Service and the Marketplace Access Agreements listed below.
          </Text>
          <Stack spacing={1} alignItems="center" textAlign="center">
            <ExternalLink to={flexAgreementUrl} underline="hover">
              Flex Marketplace Access Agreement
            </ExternalLink>
            <ExternalLink to={hiringAgreementUrl} underline="hover">
              Hiring Marketplace Access Agreement
            </ExternalLink>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
        <Stack spacing={1} alignItems="center" textAlign="center">
          <LoadingButton
            variant="contained"
            size="large"
            sx={{ textTransform: "uppercase", px: 4, borderRadius: 6 }}
            disabled={isLoadingCreateWorkplaceAgreement}
            isLoading={isLoadingCreateWorkplaceAgreement}
            onClick={onAgree}
          >
            AGREE AND ACCEPT
          </LoadingButton>
          <Text variant="body2">
            <ExternalLink to={SUPPORT_WEBFORM_URL} underline="hover">
              Contact Support
            </ExternalLink>
          </Text>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
